import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';

export default function History() {
  const { pageData, loading, error } = usePageData('history');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-slate-500">Failed to load page content</p>
        </div>
      </Layout>
    );
  }

  const { hero, content, sections, cta, seo } = pageData;
  const timeline = sections?.timeline || [];
  const heritage = sections?.heritage || {};

  return (
    <Layout>
      <SEO 
        title={seo?.title || hero?.title}
        description={seo?.description || hero?.subtitle}
        image={seo?.image || hero?.image}
        url="/history"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={hero?.image}
            alt={hero?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="history-page-title">
            {hero?.title}
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            {hero?.subtitle}
          </p>
        </div>
      </section>

      {/* History Content */}
      <ContentSection bgColor="bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            {content && (
              <div 
                className="text-lg text-slate-600 leading-relaxed mb-8"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
              />
            )}

            {/* Timeline */}
            {timeline.length > 0 && (
              <div className="space-y-12 mt-12">
                {timeline.map((item) => (
                  <div key={`timeline-${item.title}`} className="relative pl-8 border-l-4 border-secondary">
                    <div className="absolute -left-3 top-0 w-6 h-6 bg-secondary rounded-full" />
                    <h3 className="font-heading text-2xl font-semibold text-primary-900 mb-3">{item.title}</h3>
                    <div 
                      className="text-slate-600 leading-relaxed prose prose-a:text-secondary prose-a:no-underline hover:prose-a:underline"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.content) }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ContentSection>

      {/* Methodist Heritage */}
      {heritage?.cards && heritage.cards.length > 0 && (
        <ContentSection bgColor="bg-slate-50">
          <SectionHeader 
            title={heritage.title || "Our Methodist Heritage"}
            subtitle={heritage.subtitle}
          />
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {heritage.cards.map((card) => (
                <div key={`heritage-${card.title}`} className="bg-white rounded-xl p-8 shadow-soft">
                  <h3 className="font-heading text-xl font-semibold text-primary-900 mb-4">
                    {card.title}
                  </h3>
                  <div 
                    className="text-slate-600 leading-relaxed prose prose-a:text-secondary"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.content) }}
                  />
                </div>
              ))}
            </div>
          </div>
        </ContentSection>
      )}

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-primary-900"} paddingY="py-16">
          <div className="text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              {cta.title}
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto">
              {cta.description}
            </p>
            <Link
              to={cta.buttonLink || "/welcome"}
              className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
              data-testid="history-visit-btn"
            >
              {cta.buttonText || "Plan Your Visit"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
